import {defineStore} from 'pinia'
import * as api from '@/service/index'
import { isTestHost } from '@/config/env'

export const EVENT_STAGE = Object.freeze({
  REGISTRATION: 'registration',
  PHASE1: 'phase1',
  PHASE2: 'phase2',
  ENDED: 'ended'
})

const DEFAULT_TIMEZONE_OFFSET = '+08:00'

const STAGE_QUERY_MAP = Object.freeze({
  '1': EVENT_STAGE.REGISTRATION,
  '2': EVENT_STAGE.PHASE1,
  '3': EVENT_STAGE.PHASE2,
  '4': EVENT_STAGE.ENDED
})

// ===== 活动阶段时间线 =====
// 唯一数据源: config.json (https://poster.xdcdn.net/xindong/form/58881.all/website/sausage/match/config.json)
//   registration_start_time -> 报名开始(缺省 = 不限制)
//   registration_end_time   -> 报名截止(不再接受新报名)
//   phase1_start_time       -> 一阶段开始(缺省 = registration_end_time, 即无过渡期)
//   gift_start_time         -> 二阶段开始(开放送礼)
//   event_end_time          -> 活动结束(展示结束遮罩; 缺省 = 不结束)
// 报名期:           [registrationStartAt, registrationEndAt]
// 过渡期:           (registrationEndAt, phase1StartAt) — 仍视为报名期, 已报名可查状态, 未报名 toast「已结束」
// 一阶段:           [phase1StartAt, phase2StartAt)
// 二阶段(开放送礼):  [phase2StartAt, eventEndAt)
// 活动结束:          [eventEndAt, ∞)
// 测试用 ?stage=1/2/3/4 可强制覆盖阶段(仅 isTestHost 生效, 见 @/config/env)
const createStageTimeline = () => ({
  registrationStartAt: 0,
  registrationEndAt: 0,
  phase1StartAt: 0,
  phase2StartAt: 0,
  eventEndAt: 0
})

export const REGISTRATION_WINDOW = Object.freeze({
  PENDING: 'pending',
  OPEN: 'open',
  CLOSED: 'closed'
})

const getStageOverrideFromQuery = () => {
  if (typeof window === 'undefined' || !isTestHost) {
    return ''
  }

  const params = new URLSearchParams(window.location.search)
  return STAGE_QUERY_MAP[params.get('stage')] || ''
}

// config.json 里的 test_default_stage 复用 STAGE_QUERY_MAP (1/2/3/4 → registration/phase1/phase2/ended)
// 仅 isTestHost 生效, 用于测试环境统一改默认阶段而不影响正式环境; 优先级低于 URL ?stage
const getStageOverrideFromConfig = (config) => {
  if (!isTestHost || !config) return ''
  const raw = config.test_default_stage
  if (raw === null || raw === undefined || raw === '') return ''
  return STAGE_QUERY_MAP[String(raw)] || ''
}

export const parseStageTime = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return 0
    }
    return value > 1e12 ? value : value * 1000
  }

  const normalized = String(value).trim()
  if (!normalized) {
    return 0
  }

  if (/^\d+$/.test(normalized)) {
    const numericValue = Number(normalized)
    return numericValue > 1e12 ? numericValue : numericValue * 1000
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(normalized)) {
    const timestamp = Date.parse(normalized.replace(' ', 'T') + DEFAULT_TIMEZONE_OFFSET)
    return Number.isNaN(timestamp) ? 0 : timestamp
  }

  const parsed = Date.parse(normalized)
  return Number.isNaN(parsed) ? 0 : parsed
}

const resolveEventStage = ({ timeline, overrideStage, now = Date.now() }) => {
  if (overrideStage) {
    return overrideStage
  }

  if (timeline.eventEndAt && now >= timeline.eventEndAt) {
    return EVENT_STAGE.ENDED
  }

  if (!timeline.registrationEndAt) {
    return EVENT_STAGE.REGISTRATION
  }

  if (timeline.phase2StartAt && now >= timeline.phase2StartAt) {
    return EVENT_STAGE.PHASE2
  }

  // 过渡期兜底: phase1 未开始时仍视为报名期(scheduleOnly=true, apply 页继续显示)
  // 未配置 phase1_start_time 时 fallback 到 registrationEndAt, 等价于无过渡期(旧行为)
  const phase1Start = timeline.phase1StartAt || timeline.registrationEndAt
  if (now < phase1Start) {
    return EVENT_STAGE.REGISTRATION
  }

  return EVENT_STAGE.PHASE1
}

const createState = () => {
  const stageOverride = getStageOverrideFromQuery()

  return {
    eventStage: stageOverride || EVENT_STAGE.REGISTRATION,
    stageReady: Boolean(stageOverride),
    stageOverride,
    stageTimeline: createStageTimeline()
  }
}

// stage 就绪信号: 供路由守卫等待, 避免 config 未加载完时误放行到未开放页签
// 有 stageOverride 时初始已就绪; 否则等 syncEventStage 调用后 resolve
let readyResolve
const readyPromise = new Promise((resolve) => { readyResolve = resolve })
const initialOverride = getStageOverrideFromQuery()
if (initialOverride) readyResolve()

export const useStageStore = defineStore('stage', {
  state: () => createState(),
  getters: {
    scheduleOnly(state) {
      if (!state.stageReady) {
        return true
      }
      return state.eventStage === EVENT_STAGE.REGISTRATION
    },
    modulesEnabled() {
      return !this.scheduleOnly
    },
    giftingEnabled(state) {
      return state.stageReady && state.eventStage === EVENT_STAGE.PHASE2
    },
    eventEnded(state) {
      return state.stageReady && state.eventStage === EVENT_STAGE.ENDED
    }
  },
  actions: {
    // 统一的报名时间窗判断: pending(未开启) / open(报名中) / closed(已结束)
    // ?stage 覆盖时优先生效: stage=1 视为报名中, stage=2/3 视为已结束
    getRegistrationWindow(now = Date.now()) {
      if (this.stageOverride) {
        return this.stageOverride === EVENT_STAGE.REGISTRATION
          ? REGISTRATION_WINDOW.OPEN
          : REGISTRATION_WINDOW.CLOSED
      }

      const { registrationStartAt, registrationEndAt } = this.stageTimeline
      if (now < registrationStartAt) {
        return REGISTRATION_WINDOW.PENDING
      }
      if (registrationEndAt && now <= registrationEndAt) {
        return REGISTRATION_WINDOW.OPEN
      }
      return REGISTRATION_WINDOW.CLOSED
    },
    // 统一的二阶段开始倒计时文案: 未开始返回 "X日X时", 已开始/无配置返回 0
    // ?stage=3 覆盖时视为已开始
    getPhase2Countdown(now = Date.now()) {
      if (this.eventStage === EVENT_STAGE.PHASE2) {
        return 0
      }

      const targetTime = this.stageTimeline.phase2StartAt
      const timeDifference = targetTime - now
      if (!targetTime || timeDifference <= 0) {
        return 0
      }

      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      return `${days}日${hours}时`
    },
    // 按服务器时间计算二阶段倒计时; 请求失败返回 null, 调用方保持原值
    async reqPhase2Countdown() {
      try {
        const response = await api.reqcurrentTime()
        if (response.status === 200) {
          return this.getPhase2Countdown(new Date(response.data).getTime())
        }
      } catch (err) {
        console.log(err)
      }
      return null
    },
    // 传入 config.json 数据时更新时间线; 不传则按已有时间线重算当前阶段
    syncEventStage(config) {
      if (config) {
        this.stageTimeline.registrationStartAt = parseStageTime(config.registration_start_time)
        this.stageTimeline.registrationEndAt = parseStageTime(config.registration_end_time)
        this.stageTimeline.phase1StartAt = parseStageTime(config.phase1_start_time)
        this.stageTimeline.phase2StartAt = parseStageTime(config.gift_start_time)
        this.stageTimeline.eventEndAt = parseStageTime(config.event_end_time)

        // URL ?stage 优先; 未指定时接入 config.test_default_stage (仅测试环境)
        if (!this.stageOverride) {
          const configOverride = getStageOverrideFromConfig(config)
          if (configOverride) this.stageOverride = configOverride
        }
      }

      this.stageReady = Boolean(this.stageOverride || this.stageTimeline.registrationEndAt)
      this.eventStage = resolveEventStage({
        timeline: this.stageTimeline,
        overrideStage: this.stageOverride
      })

      // 无论成功与否都释放 whenReady 等待, 避免路由守卫卡死
      // (config 拉不到时 stageReady 仍是 false, 守卫走"不就绪即放行"的老路径)
      if (readyResolve) {
        readyResolve()
        readyResolve = null
      }

      return this.eventStage
    },
    // 等 stage 就绪 (config 加载完成或加载失败都会 resolve, 决不 hang)
    whenReady() {
      return readyPromise
    }
  }
})
